import { useMemo, useEffect } from 'react';
import { Col, Form, Row } from 'antd';
import { Link, useLocation, useParams } from 'react-router-dom';
import { Button } from 'components';
import { DeleteIcon, EditIcon } from 'icons';
import { useTranslation } from 'react-i18next';
import { EyeOutlined } from '@ant-design/icons';
import { useNavigate } from 'hooks/useNavigate';
import { useRecoilValue } from 'recoil';
import { getShoppingCenters } from 'hooks/useUser';
import { useApi } from 'hooks/useApi';
import FacilityExtrastockContractorsApi from 'libs/api/facility/extrastockContractorsApi';
import UIHelper from 'helpers/UIHelper';
import ErrorHelper from 'helpers/ErrorHelper';

const api = new FacilityExtrastockContractorsApi();

const useExtrastockContractors = (isMainPage = true, isEdit) => {
  const { t } = useTranslation();
  let { id } = useParams();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const shoppingCenters = useRecoilValue(getShoppingCenters);
  const [form] = Form.useForm();
  const {
    data,
    onChangeTableFilter,
    loading,
    updateInProcess,
    handleDownloadExcel,
    singleItem,
    setUpdateInProcess,
    onDelete,
  } = useApi({
    resourceKey: 'extrastock_contractors',
    singleResourceKey: 'extrastock_contractor',
    api: api,
    isMainPage,
  });

  const columns = useMemo(() => {
    return [
      {
        title: t('Titolo'),
        dataIndex: 'title',
        fixed: 'left',
        sorter: () => {},
      },
      {
        title: '',
        dataIndex: 'address',
        align: 'right',
        width: 180,
        fixed: 'right',
        render: (data, row) => (
          <Row
            gutter={16}
            justify={'center'}
            onClick={(e) => e.stopPropagation()}
          >
            <Col>
              <Link to={`${pathname}/${row.id}`}>
                <Button icon={<EyeOutlined />} />
              </Link>
            </Col>
            {row.can_update !== false && (
              <Col>
                <Link to={`${pathname}/${row.id}/edit`}>
                  <Button type={'secondary'} icon={<EditIcon />} />
                </Link>
              </Col>
            )}
            {row.can_destroy !== false && (
              <Col>
                <Link to={`${pathname}/${row.id}/delete`}>
                  <Button type={'red'} icon={<DeleteIcon />} />
                </Link>
              </Col>
            )}
          </Row>
        ),
      },
    ];
  }, [pathname]);

  const filters = useMemo(
    () => [
      {
        title: t('Cerca'),
        type: 'input',
        name: 'by_title',
        isActiveSearch: true,
      },
    ],
    [t],
  );

  const handleCreateOrUpdate = async (values) => {
    setUpdateInProcess(true);
    const data = {
      extrastock_contractor: {
        ...values,
      },
    };
    try {
      if (!id) {
        await api.create({ data });
        navigate(UIHelper.getListRoute(pathname, 1));
      } else {
        await api.put(id, { data });
        navigate(UIHelper.getListRoute(pathname));
      }
    } catch (e) {
      const errors = e.response.data || [];
      form.setFields(
        ErrorHelper.getErrorsForForm(errors, {
          company: 'company_id',
        }),
      );
    } finally {
      setUpdateInProcess(false);
    }
  };

  useEffect(() => {
    (async () => {
      if (id) {
        try {
          if (isEdit) {
            let fieldsValues = [];
            for (let key in singleItem) {
              switch (key) {
                case 'shopping_centers': {
                  const ids = singleItem[key].map((i) => i.id);
                  fieldsValues.push({
                    name: 'shopping_center_ids',
                    value: ids,
                  });
                  break;
                }
                default: {
                  fieldsValues.push({
                    name: key,
                    value: singleItem[key],
                  });
                }
              }
            }
            form.setFields(fieldsValues);
          }
        } catch (e) {
          console.log(e);
        }
      }
    })();
  }, [singleItem]);

  return {
    columns,
    data,
    onChangeTableFilter,
    loading,
    filters,
    onDelete,
    updateInProcess,
    shoppingCenters,
    form,
    handleCreateOrUpdate,
    handleDownloadExcel,
    singleItem,
  };
};

export default useExtrastockContractors;
